import {useCallback, useEffect, useRef} from 'react';

const useSlideToggle = <T extends HTMLElement = HTMLElement>(
	shouldWork: boolean = true,
	shouldScrollIntoView: boolean = true
) => {
	const togglerRef = useRef<T>(null);

	const handleHide = useCallback(() => {
		if (!shouldWork || !togglerRef.current) {
			return;
		}

		togglerRef.current.style.opacity = '0';
		togglerRef.current.style.height = '0';
		togglerRef.current.style.overflow = 'hidden';
	}, [shouldWork]);

	const handleShow = useCallback((height: number) => {
		if (!shouldWork || !togglerRef.current) {
			return;
		}

		togglerRef.current.style.transition = 'all 0.5s ease-in-out';
		togglerRef.current.style.opacity = '1';
		togglerRef.current.style.height = `${height}px`;

		if (!shouldScrollIntoView) {
			return;
		}

		setTimeout(() => {
			// To avoid margins
			togglerRef.current?.parentElement?.parentElement?.scrollIntoView({behavior: 'smooth', block: 'start'});
		}, 300);
	}, [shouldWork]);

	const cleanUp = useCallback(() => {
		if (!togglerRef.current) {
			return;
		}

		togglerRef.current.style.transition = 'none';
		togglerRef.current.style.opacity = '1';
		togglerRef.current.style.height = 'auto';
		togglerRef.current.style.overflow = 'visible';
	}, []);

	const handleSlideToggle = useCallback((isOpen: boolean) => {
		if (!shouldWork || !togglerRef.current) {
			return;
		}

		const childEl = togglerRef.current.children[0] as HTMLElement;

		if (isOpen) {
			handleShow(childEl?.offsetHeight);
		} else {
			handleHide();
		}
	}, [shouldWork]);

	useEffect(() => {
		if (shouldWork) {
			handleHide();
		} else {
			cleanUp();
		}

		return () => {
			cleanUp();
		};
	}, [togglerRef, shouldWork]);

	return {togglerRef, handleSlideToggle};
};

export default useSlideToggle;
